import type { Destination } from '../destination';
import { lazySdk } from '../lazy-sdk';
import { toVendorEvent } from '../mapping';

/**
 * Meta Pixel: `screen_view` is the pixel's page view, every other event a
 * custom event with the row as its data. Pass-through: no insights guarantee.
 */
export function createMetaPixelDestination(pixelId: string): Destination {
	let revoked = false;
	const pixel = lazySdk(
		() =>
			import('react-facebook-pixel').then((module) => {
				// The package is a webpack UMD bundle whose export is already
				// `{ __esModule, default }`; Vite's dev pre-bundler unwraps one
				// layer of that and the production build both, so take whichever
				// level actually has the API.
				const outer = module.default as typeof module.default & {
					default?: typeof module.default;
				};
				return outer.default ?? outer;
			}),
		(ReactPixel) => ReactPixel.init(pixelId, undefined, { autoConfig: true, debug: false }),
	);

	return {
		name: 'metaPixel',
		start: () => {
			pixel.load();
			if (revoked) {
				revoked = false;
				pixel.use((ReactPixel) => ReactPixel.grantConsent());
			}
		},
		stop: () => {
			revoked = true;
			pixel.use((ReactPixel) => ReactPixel.revokeConsent());
		},
		send: (event) => {
			const { name, params } = toVendorEvent(event);
			pixel.use((ReactPixel) => {
				if (event.name === 'screen_view') {
					ReactPixel.pageView();
				} else {
					ReactPixel.trackCustom(name, params);
				}
			});
		},
	};
}
